/**
 * My Accountant Page — Seafarer's hub for accountant linking and document sharing.
 * Sub-tabs: Overview | Documents
 * Messages have been moved to the dedicated /messages page.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  UserCheck, Mail, Copy, Check, Clock, Building, UserX, Link2, Send,
  MessageSquare, FileText, Upload, Loader2, Paperclip, ChevronDown,
  Ship, Briefcase, AlertCircle, CheckCircle2, X, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_CATEGORIES = [
  { value: 'payslip', label: 'Wage Slip / Payslip' },
  { value: 'p60', label: 'P60' },
  { value: 'p45', label: 'P45' },
  { value: 'boarding_pass', label: 'Boarding Pass' },
  { value: 'flight_ticket', label: 'Flight Ticket' },
  { value: 'contract', label: 'Contract' },
  { value: 'receipt', label: 'Expense Receipt' },
  { value: 'other', label: 'Other' },
];

/* ──────────── Overview Tab ──────────── */
const OverviewTab = ({ linkStatus, pendingLinks, onRefresh }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMsg, setInviteMsg] = useState('');
  const [inviting, setInviting] = useState(false);
  const [linkCode, setLinkCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await axios.post(`${API_URL}/api/client-link/invite-accountant`, {
        email: inviteEmail.trim().toLowerCase(),
        message: inviteMsg.trim()
      });
      toast.success(res.data.message);
      setInviteEmail('');
      setInviteMsg('');
      setLinkCode({ code: res.data.code, expires_at: res.data.expires_at });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const generateCode = async () => {
    setGeneratingCode(true);
    try {
      const res = await axios.post(`${API_URL}/api/client-link/generate`);
      setLinkCode(res.data);
      toast.success('Link code generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    toast.success('Code copied');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const confirmLink = async (linkId) => {
    try {
      await axios.post(`${API_URL}/api/client-link/confirm`, { link_id: linkId, consent_given: true });
      toast.success('Accountant linked successfully');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    }
  };

  const revokeLink = async () => {
    if (!window.confirm('Revoke accountant access? They will immediately lose access to your data.')) return;
    try {
      await axios.post(`${API_URL}/api/client-link/revoke`);
      toast.success('Accountant access revoked');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  if (linkStatus?.linked) {
    return (
      <div className="space-y-4">
        <Card className="bg-sky-500/5 border-sky-500/20">
          <CardContent className="p-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-sky-500/10">
                  <Building className="h-5 w-5 text-sky-400" />
                </div>
                <div>
                  <p className="text-white font-semibold">{linkStatus.accountant?.firm_name}</p>
                  {linkStatus.accountant?.contact_name && (
                    <p className="text-slate-400 text-sm">{linkStatus.accountant.contact_name}</p>
                  )}
                  <p className="text-slate-500 text-xs mt-0.5">
                    <CheckCircle2 className="h-3 w-3 inline mr-0.5 text-emerald-400" />
                    Linked since {new Date(linkStatus.accountant?.linked_since).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={revokeLink}
                className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="revoke-accountant-btn">
                <UserX className="h-3.5 w-3.5 mr-1.5" />Revoke Access
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">GDPR Consent Given</p>
            <p className="text-xs text-slate-400">{linkStatus.accountant?.consent_text}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pending confirmations */}
      {pendingLinks.length > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-400" />Pending Confirmation
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0 space-y-2">
            {pendingLinks.map(link => (
              <div key={link.link_id} className="p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
                <p className="text-sm font-medium text-white">{link.firm_name}</p>
                <p className="text-xs text-slate-500">HMRC ARN: {link.hmrc_arn}</p>
                <div className="p-2 bg-slate-800/50 rounded text-xs text-slate-400 mt-2 mb-2">
                  <p className="font-medium text-amber-300 mb-1">GDPR Consent:</p>
                  <p>{link.consent_text}</p>
                </div>
                <Button size="sm" onClick={() => confirmLink(link.link_id)}
                  className="bg-sky-600 hover:bg-sky-700 h-7 text-xs" data-testid={`confirm-link-${link.link_id}`}>
                  <Check className="h-3 w-3 mr-1" />Confirm & Grant Access
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Invite Your Accountant */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Mail className="h-4 w-4 text-amber-400" />Invite Your Accountant
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <p className="text-xs text-slate-400 mb-3">
            Send your accountant an email invitation with a unique link code. They can use this to register on the Accountant Portal and securely access your SED data.
          </p>
          <form onSubmit={handleInvite} className="space-y-3">
            <Input type="email" placeholder="Accountant's email address" value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)} required
              className="bg-slate-900 border-slate-600 text-white h-9 text-sm" data-testid="invite-accountant-email" />
            <Input type="text" placeholder="Personal message (optional)" value={inviteMsg}
              onChange={e => setInviteMsg(e.target.value)}
              className="bg-slate-900 border-slate-600 text-white h-9 text-sm" data-testid="invite-accountant-message" />
            <Button type="submit" disabled={inviting || !inviteEmail.trim()}
              className="w-full bg-amber-600 hover:bg-amber-700 h-9 text-sm" data-testid="send-accountant-invite">
              {inviting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send Invitation
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Generate Link Code (manual) */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Link2 className="h-4 w-4 text-sky-400" />Share a Link Code
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <p className="text-xs text-slate-400 mb-3">
            Generate a unique code and share it with your accountant directly. Each code is unique and expires after 48 hours.
          </p>
          {linkCode ? (
            <div className="p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-center">
              <p className="text-xs text-slate-500 mb-1">Your Link Code</p>
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-xl font-mono font-bold text-sky-400 tracking-wider" data-testid="link-code-display">{linkCode.code}</span>
                <button onClick={() => copyCode(linkCode.code)} className="text-slate-400 hover:text-white" data-testid="copy-link-code">
                  {copiedCode ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-[10px] text-slate-600 flex items-center justify-center gap-1">
                <Clock className="h-3 w-3" />Expires in 48 hours
              </p>
              <Button variant="outline" size="sm" onClick={generateCode} disabled={generatingCode}
                className="mt-2 border-slate-600 text-slate-400 h-7 text-xs" data-testid="generate-new-code">
                Generate New Code
              </Button>
            </div>
          ) : (
            <Button onClick={generateCode} disabled={generatingCode}
              className="w-full bg-sky-600 hover:bg-sky-700 h-9 text-sm" data-testid="generate-link-code-btn">
              {generatingCode ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
              Generate Link Code
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


/* ──────────── Documents Tab ──────────── */
const DocumentsTab = ({ linked }) => {
  const [docs, setDocs] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('payslip');
  const [selectedTrip, setSelectedTrip] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showTrips, setShowTrips] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [docsRes, tripsRes] = await Promise.all([
        axios.get(`${API_URL}/api/client-link/shared-documents`),
        axios.get(`${API_URL}/api/client-link/trips-for-linking`)
      ]);
      setDocs(docsRes.data.documents || []);
      setTrips(tripsRes.data.trips || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (linked) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [linked, fetchData]);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error('File must be under 15MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setFile({ name: f.name, content: reader.result.split(',')[1] });
      // Auto-detect trip by date in filename
      autoDetectTrip(f.name);
    };
    reader.readAsDataURL(f);
  };

  const autoDetectTrip = (filename) => {
    // Try to match dates in filename to trip dates
    const datePatterns = [
      /(\d{4})-(\d{2})-(\d{2})/,  // 2025-04-15
      /(\d{2})[-\/](\d{2})[-\/](\d{4})/,  // 15-04-2025
    ];
    for (const pattern of datePatterns) {
      const match = filename.match(pattern);
      if (match) {
        // Find matching trip
        for (const trip of trips) {
          const left = trip.date_left_uk || trip.date_joined_vessel;
          const returned = trip.date_returned_uk || trip.date_left_vessel;
          if (left && returned) {
            // Simple check: does the date in filename fall within trip dates?
            const fileDate = match[0];
            if (fileDate >= left && fileDate <= returned) {
              setSelectedTrip(trip.id);
              toast.info(`Auto-detected: ${trip.vessel_name || 'Trip'} (${left} to ${returned})`);
              return;
            }
          }
        }
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      // 1. Upload the document
      const docRes = await axios.post(`${API_URL}/api/documents/upload`, {
        file_name: file.name,
        file_content: file.content,
        document_type: category === 'payslip' ? 'payslips' : category === 'receipt' ? 'receipts' : category === 'boarding_pass' || category === 'flight_ticket' ? 'travel' : category,
        tax_year: '',
        month: ''
      });

      const docId = docRes.data.document?.id;

      // 2. Link to trip if selected
      if (selectedTrip && docId) {
        await axios.post(`${API_URL}/api/documents/link`, {
          document_id: docId,
          trip_id: selectedTrip
        });
      }

      // 3. Notify accountant via message
      if (linked) {
        const catLabel = DOC_CATEGORIES.find(c => c.value === category)?.label || category;
        const tripName = trips.find(t => t.id === selectedTrip)?.vessel_name;
        await axios.post(`${API_URL}/api/client-link/messages`, {
          content: `Uploaded ${catLabel}: ${file.name}${tripName ? ` (linked to ${tripName})` : ''}`,
          attachment_type: 'document',
          attachment_id: docId
        });
      }

      toast.success('Document uploaded and shared with your accountant');
      setFile(null);
      setSelectedTrip('');
      setCategory('payslip');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  if (!linked) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-8 text-center">
          <FileText className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Link your accountant to share documents</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Upload Section */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Upload className="h-4 w-4 text-amber-400" />Upload Document
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-3">
          <p className="text-xs text-slate-400">
            Upload missing paperwork here. It will be shared with your accountant and linked to the relevant trip on your record.
          </p>

          {/* File Picker */}
          <div onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-slate-600 hover:border-amber-500/40 rounded-lg p-4 text-center cursor-pointer transition-colors"
            data-testid="file-drop-zone">
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv" />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText className="h-5 w-5 text-amber-400" />
                <span className="text-sm text-white">{file.name}</span>
                <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-slate-500 hover:text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div>
                <Upload className="h-6 w-6 text-slate-500 mx-auto mb-1" />
                <p className="text-xs text-slate-400">Click to select a file (PDF, image, or document)</p>
              </div>
            )}
          </div>

          {file && (
            <>
              {/* Category */}
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Document Type</Label>
                <select value={category} onChange={e => setCategory(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-md h-9 text-sm px-3"
                  data-testid="doc-category-select">
                  {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              {/* Trip Linking */}
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Link to Trip (auto-detected or choose manually)</Label>
                <select value={selectedTrip} onChange={e => setSelectedTrip(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 text-white rounded-md h-9 text-sm px-3"
                  data-testid="doc-trip-select">
                  <option value="">No trip selected</option>
                  {trips.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.vessel_name || t.trip_type} — {t.date_left_uk || t.date_joined_vessel} to {t.date_returned_uk || t.date_left_vessel}
                    </option>
                  ))}
                </select>
                {selectedTrip && (
                  <p className="text-[10px] text-emerald-400 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Document will be linked to this trip and visible on the trip card
                  </p>
                )}
              </div>

              <Button onClick={handleUpload} disabled={uploading}
                className="w-full bg-amber-600 hover:bg-amber-700 h-9 text-sm" data-testid="upload-doc-btn">
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload & Share
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Recent Documents */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <FileText className="h-4 w-4 text-sky-400" />Shared Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>
          ) : docs.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No documents uploaded yet</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {docs.slice(0, 20).map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-2.5 bg-slate-900/40 rounded-lg border border-slate-700/50">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-amber-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{doc.original_file_name || doc.file_name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        <span>{doc.document_type}</span>
                        {doc.linked_trip_id && (
                          <span className="text-sky-400 flex items-center gap-0.5">
                            <Ship className="h-2.5 w-2.5" />Linked
                          </span>
                        )}
                        <span>{new Date(doc.uploaded_at).toLocaleDateString('en-GB')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


/* ──────────── Main Page ──────────── */
const MyAccountant = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [linkStatus, setLinkStatus] = useState(null);
  const [pendingLinks, setPendingLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLinkData = useCallback(async () => {
    try {
      const [statusRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/api/client-link/status`),
        axios.get(`${API_URL}/api/client-link/pending`)
      ]);
      setLinkStatus(statusRes.data);
      setPendingLinks(pendingRes.data.pending || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinkData();
  }, [fetchLinkData]);

  const tabs = [
    { id: 'overview', icon: UserCheck, label: 'Overview' },
    { id: 'documents', icon: FileText, label: 'Documents' },
  ];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="my-accountant-page">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Briefcase className="h-7 w-7 text-amber-400" />My Accountant
        </h1>
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="my-accountant-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Briefcase className="h-7 w-7 text-amber-400" />My Accountant
        </h1>
        <p className="text-slate-400 mt-1 text-sm">
          {linkStatus?.linked
            ? `Linked to ${linkStatus.accountant?.firm_name}`
            : 'Invite or link your accountant to share data securely'}
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 bg-slate-800/60 p-1 rounded-lg w-fit" data-testid="accountant-tabs">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-amber-600 text-white shadow-md shadow-amber-600/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
            data-testid={`accountant-tab-${tab.id}`}>
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab linkStatus={linkStatus} pendingLinks={pendingLinks} onRefresh={fetchLinkData} />}
      {activeTab === 'documents' && <DocumentsTab linked={linkStatus?.linked} />}
    </div>
  );
};

export default MyAccountant;
